import PropTypes from 'prop-types';
import cn from 'classnames';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';

import { renderNode } from '../../helpers';
// import { useAdjustedHeight } from './useAdjustedHeight';

import styles from './ModalSheet.scss';

const variants = {
    root: {
        visible: { pointerEvents: 'auto' },
        hidden: { pointerEvents: 'none' },
    },
    overlay: {
        visible: { backgroundColor: 'rgba(0,0,0,.5)' },
        hidden: { backgroundColor: 'rgba(0,0,0,0)' },
    },
    sheet: {
        visible: { y: 0 },
        hidden: { y: 'calc(100% + 20px)' },
    },
};

const transitions = {
    overlay: { ease: 'easeInOut' },
    sheet: {
        type: 'spring',
        damping: 40,
        stiffness: 400,
    },
    sheetPane: {
        type: 'spring',
        damping: 40,
        stiffness: 400,
    },
};

export function ModalSheet({
    children,
    isClosing,
    onClose,
    onExit,
    fullWidth = true,
    fullHeight,
    inverse,
    paneMotionProps,
    sheetPaneProps,
    ...rest
}) {
    const history = useHistory();
    const handleClosed = useCallback(() => ModalSheet.applyClose(onClose, history), [onClose, history]);
    // const sheetStyle = useAdjustedHeight(-(fullHeight ? 0 : fullWidth ? 16 : 32));

    return (
        <motion.div
            className={cn(styles.root, { [styles.rootLegacy]: __LEGACY__ })}
            variants={variants.root}
            initial="hidden"
            animate={isClosing ? 'hidden' : 'visible'}
            enter="visible"
            exit="hidden"
            {...rest}
        >
            <motion.div
                className={styles.overlay}
                variants={variants.overlay}
                transition={transitions.overlay}
                initial="hidden"
                animate={isClosing ? 'hidden' : 'visible'}
                exit="hidden"
                enter="visible"
                onClick={handleClosed}
                onAnimationComplete={isClosing ? onExit : undefined}
            />
            <motion.div
                className={cn({
                    [styles.sheet]: true,
                    [styles.fullWidth]: !__LEGACY__ && fullWidth,
                    [styles.fullHeight]: fullHeight,
                    [styles.sheetLegacy]: __LEGACY__,
                })}
                // style={sheetStyle}
                variants={variants.sheet}
                transition={transitions.sheet}
                initial="hidden"
                animate={isClosing ? 'hidden' : 'visible'}
                exit="hidden"
                enter="visible"
            >
                <motion.div
                    className={cn(styles.sheetPane, inverse && styles.inverse)}
                    dragMomentum={false}
                    transition={transitions.sheetPane}
                    {...paneMotionProps}
                    {...sheetPaneProps}
                >
                    {renderNode(children, handleClosed)}
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

ModalSheet.propTypes = {
    children: PropTypes.any.isRequired,
    isClosing: PropTypes.bool,
    onClose: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.object]),
    onExit: PropTypes.func,
    paneMotionProps: PropTypes.object,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    inverse: PropTypes.bool,
    sheetPaneProps: PropTypes.object,
};

ModalSheet.applyClose = (onClose, history) => {
    if (typeof onClose === 'function') {
        onClose();
    } else if (onClose) {
        history.push(onClose);
    }
};
